import { ClassNames } from '@emotion/core';
import React from 'react';

import { Colors } from '@zapier/design-system';

type LogoImageProps = {
  /**
   * The class that will be applied to the element. Usually this is the name of the service,
   * for example 'CodeAPI'. There is some backend magic that generates a .css file, maping
   * this class name to an image file on the CDN contain the service logo. The background-image
   * style will point to that image file.
   *
   * If the service isn't provided the logo will default to the grey Zapier placeholder logo.
   *
   * If the image for that service can't be found it will default to the grey Zapier placeholder
   * logo.
   */
  app: string | null | undefined;
};

// @TODO: Remove this hack once we figure out how to properly deprecate Python apps.
// Google Sheets was replaced and that caused the icon to disappear.
// So adding it back in via emergency hack.
// https://zapier.slack.com/archives/C03TB1UMA/p1490971184595755
const API_IMAGES = {
  AppTesterAPI:
    "url('https://cdn.zapier.com/storage/services/4401a6c0fdab0b69893ab957f2712b81.png')",
  BranchingAPI:
    "url('https://cdn.zapier.com/storage/services/137baf61724ea2c64812417382bddd1d.128x128.png')",
  CapsuleCRMAPI:
    "url('https://cdn.zapier.com/storage/services/1029d32eb05260b4a52ba6eebf5dc20f.png')",
  EventbriteAPI:
    "url('https://cdn.zapier.com/storage/photos/4b2b5f32f92c80fc304ab369be1da347.png')",
  EventbriteDevAPI:
    "url('https://cdn.zapier.com/storage/developer/638ebef07f1e312e20ee45ddb7df6be5.png')",
  GoogleContactsV2API:
    "url('https://cdn.zapier.com/storage/services/1508661b55cd3c5ad1787303b0f58c99.png')",
  GoogleFormsAPI:
    "url('https://cdn.zapier.com/storage/services/2554f3f9544cf8375a46f68b18de0e7d.128x128.png')",
  GoogleSheetsAPI:
    "url('https://cdn.zapier.com/storage/services/e37713ab22d82f777575279db97cd805.128x128.png')",
  MailChimpAPI:
    "url('https://cdn.zapier.com/storage/services/5c727288d9c2f69a9eee136c5f5a0f72.128x128.png')",
  OneNoteDevAPI:
    "url('https://cdn.zapier.com/storage/developer/e951e0105790e43b117e04b7bba34601.png')",
  PivotalTrackerAPI:
    "url('https://cdn.zapier.com/storage/services/84172dde558de382873cf72a00c92818.128x128.png')",
  QuickBaseAPI:
    "url('https://cdn.zapier.com/storage/services/3d348ba36ddb1a656e434ccbb984e3c6.png')",
  SkyDriveAPI:
    "url('https://cdn.zapier.com/storage/services/e67867e9f6f5c089e243dad54467ad33.128x128.png')",
  SurveyMonkeyAPI:
    "url('https://cdn.zapier.com/storage/services/39b027caffb7fad05f73ed80d664a246.128x128.png')",
  TypeformDevAPI:
    "url('https://cdn.zapier.com/storage/developer/6df0e2a9ca7350c7407f0d9c2b0709c0.png')",
  ZapierInternalAPI:
    "url('https://cdn.zapier.com/storage/photos/ff7409c6a52dc217fe9829531c3a66dd.png')",
  ZohoCRMAPI:
    "url('https://images.zapier.com/storage/services/58719c2f59576a993b6c6855e0f585df.128x128.png')",
};

const stripVersion = (name: string | null | undefined): string =>
  name?.split('@')?.[0] || '';

export const LogoImage = ({ app }: LogoImageProps) => {
  const appUnversioned = stripVersion(app);
  const imageSize = 128;
  const sizeXsize = `${imageSize}x${imageSize}`;
  const sizeWithPx = `${imageSize}px`;
  const adjustedSizeWithPx = `${imageSize + 10 * 2}px`;

  const styles = {
    backgroundColor: Colors.white,
    // @ts-ignore
    backgroundImage: API_IMAGES[appUnversioned],
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${sizeWithPx} ${sizeWithPx}`,
    border: '1px solid #dadfe2',
    borderRadius: 40,
    boxShadow: '0px 0px 30px rgba(0, 0, 0, 0.1)',
    display: 'inline-block',
    height: adjustedSizeWithPx,
    minWidth: adjustedSizeWithPx,
    verticalAlign: 'middle',
  };

  const classes = [
    `PlaceholderAPI${sizeXsize}`,
    `${appUnversioned}${sizeXsize}`,
  ];

  // This is a bit hackey but we need to include the app as a class for the div.
  // Logo images are stored in the backend-generated files:
  //   - global-logos.css
  //   - private-logos.css
  return (
    <ClassNames>
      {({ css, cx }) => <div className={cx(css(styles), classes)} />}
    </ClassNames>
  );
};
